/**
 * useOrbDocDownloadsCliSLDocBodyLogic
 *
 * Pure logic hook — state machine, orbital bridge, actions.
 * Use the companion component for rendering.
 *
 * @generated from schema
 * @trait OrbDocDownloadsCliSLDocBody
 */

import { useReducer, useCallback, useEffect, useRef, useMemo } from 'react';
import { useOrbitalBridge } from '@/hooks/useOrbitalBridge';
import type { EntityRow, EventPayload, EventPayloadValue, FieldValue } from '@almadar/core';
function getNestedValue(obj: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined, path: string): FieldValue | undefined {
  if (obj === null || obj === undefined || !path) return undefined;
  if (typeof obj !== 'object') return undefined;
  if (!path.includes('.') && !Array.isArray(obj)) {
    return (obj as Record<string, FieldValue | undefined>)[path];
  }
  let value: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined = obj;
  for (const part of path.split('.')) {
    if (value === null || value === undefined || typeof value !== 'object') return undefined;
    if (Array.isArray(value)) {
      if (!/^\d+$/.test(part)) return undefined;
      value = (value as readonly (EventPayloadValue | FieldValue | null | undefined)[])[Number(part)];
      continue;
    }
    value = (value as Record<string, FieldValue | undefined>)[part];
  }
  return value as FieldValue | undefined;
}
import type { EventResponse, OrbDocDownloadsCliSLDocView as OrbDocDownloadsCliSLDocViewEntity, OrbDocDownloadsCliSLDocBodyEventPayloadMap } from '@app/shared';
import { useUIEvents } from '@almadar/ui/hooks';
import { registerTraitSnapshot, type TraitStateSnapshot } from '@almadar/ui/lib';
import { createCommandSendPump } from '@almadar/ui/lib';

// ============================================================================
// State Machine
// ============================================================================

export type OrbDocDownloadsCliSLDocBodyState =
  | 'idle';

export type OrbDocDownloadsCliSLDocBodyEvent =
  | 'INIT';

type OrbDocDownloadsCliSLDocBodySlot = 'main';
interface OrbDocDownloadsCliSLDocBodyInternalState {
  machineState: OrbDocDownloadsCliSLDocBodyState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: OrbDocDownloadsCliSLDocBodyEvent | null;
  slotWriter: Partial<Record<OrbDocDownloadsCliSLDocBodySlot, OrbDocDownloadsCliSLDocBodyEvent | null>>;
  loading: boolean;
  pendingEvent: OrbDocDownloadsCliSLDocBodyEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<OrbDocDownloadsCliSLDocViewEntity>;
}

type OrbDocDownloadsCliSLDocBodyAction =
  | { type: 'EVENT_SUCCESS'; event: OrbDocDownloadsCliSLDocBodyEvent; newState: string; data: Record<string, EntityRow[]>; payload?: EventPayload; fields?: Record<string, EventPayloadValue | undefined>; renderBearing?: boolean; renderWrite?: OrbDocDownloadsCliSLDocBodySlot[]; renderClear?: OrbDocDownloadsCliSLDocBodySlot[] }
  | { type: 'EVENT_ERROR'; error: string }
  | { type: 'RESET_ERROR' }
  | { type: 'START_EVENT'; event: OrbDocDownloadsCliSLDocBodyEvent; from: string }
  | { type: 'END_EVENT' }
  | { type: 'SET_FIELD'; field: keyof OrbDocDownloadsCliSLDocViewEntity & string; value: EventPayloadValue | undefined }
  | { type: 'TICK'; updates: Record<string, EventPayloadValue | undefined>; collection: string };

function orbDocDownloadsCliSLDocBodyReducer(
  state: OrbDocDownloadsCliSLDocBodyInternalState,
  action: OrbDocDownloadsCliSLDocBodyAction
): OrbDocDownloadsCliSLDocBodyInternalState {
  switch (action.type) {
    case 'START_EVENT':
      return { ...state, loading: true, pendingEvent: action.event, pendingFrom: action.from };
    case 'END_EVENT':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null };
    case 'EVENT_SUCCESS':
      return {
        machineState: action.newState as OrbDocDownloadsCliSLDocBodyState,
        data: { ...state.data, ...action.data },
        lastPayload: action.renderBearing ? (action.payload ?? state.lastPayload) : state.lastPayload,
        lastEvent: action.renderBearing ? action.event : state.lastEvent,
        slotWriter: {
          ...state.slotWriter,
          ...Object.fromEntries((action.renderWrite ?? []).map((s) => [s, action.event] as const)),
          ...Object.fromEntries((action.renderClear ?? []).map((s) => [s, null] as const)),
        },
        loading: false,
        pendingEvent: null,
        pendingFrom: null,
        error: null,
        fields: { ...state.fields, ...(action.fields ?? {}) } as Partial<OrbDocDownloadsCliSLDocViewEntity>,
      };
    case 'EVENT_ERROR':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null, error: action.error };
    case 'RESET_ERROR':
      return { ...state, error: null };
    case 'SET_FIELD': {
      // Back-compat for non-codegen consumers that still dispatch
      // SET_FIELD directly. Codegen folds field updates into
      // EVENT_SUCCESS so transitions commit in a single render.
      return {
        ...state,
        fields: { ...state.fields, [action.field]: action.value } as Partial<OrbDocDownloadsCliSLDocViewEntity>,
      };
    }
    case 'TICK': {
      // Batch-update entity fields for tick effects. A [runtime]-linked
      // trait's row lives in the `fields` mirror (transitions write it,
      // the view reads it), so updates ALWAYS merge there; the
      // `data` collection row is updated too when one exists.
      const tickFields = { ...state.fields, ...action.updates } as Partial<OrbDocDownloadsCliSLDocViewEntity>;
      const arr = state.data[action.collection];
      const target = arr?.[0];
      if (!target) return { ...state, fields: tickFields };
      const updated = { ...target, ...action.updates } as EntityRow;
      return {
        ...state,
        data: { ...state.data, [action.collection]: [updated, ...arr.slice(1)] },
        fields: tickFields,
      };
    }
    default:
      return state;
  }
}

const initialState: OrbDocDownloadsCliSLDocBodyInternalState = {
  machineState: 'idle',
  data: {},
  lastPayload: undefined,
  lastEvent: null,
  slotWriter: {},
  loading: false,
  pendingEvent: null,
  pendingFrom: null,
  error: null,
  fields: {},
};

// ============================================================================
// Hook
// ============================================================================

import type { DocNavItem } from '@app/shared';
export interface OrbDocDownloadsCliSLDocBodyConfig {
  linkedEntity?: string;
  entityId?: string;
  routeParams?: Record<string, string | undefined>;
  initPayload?: EventPayload;
  content: { children: Array<{ children: Array<{ className: string; content: string; type: string; variant: string; weight: string } | { code: string; language: string; maxHeight: string; showCopyButton: boolean; showLineNumbers: boolean; type: string } | { content: string; direction: string; type: string }>; direction: string; gap: string; id: string; type: string } | { children: Array<{ className: string; content: string; type: string; variant: string; weight: string } | { code: string; language: string; maxHeight: string; showCopyButton: boolean; showLineNumbers: boolean; type: string }>; direction: string; gap: string; id: string; type: string } | { content: string; direction: string; type: string }>; direction: string; gap: string; type: string };
  crumbs: Array<{ href: string; label: string } | { label: string }>;
  direction: 'ltr' | 'rtl';
  lede: string;
  navItems: Array<{ items: Array<{ active: boolean; href: string; label: string } | { items: { active: boolean; href: string; label: string }[]; label: string }>; label: string } | { items: { active: boolean; href: string; label: string }[]; label: string }>;
  next: { href: string; label: string };
  prev: { href: string; label: string };
  sidebarLabel: string;
  title: string;
  toc: { id: string; label: string; level: number }[];
  tocLabel: string;
}

const DEFAULT_ORB_DOC_DOWNLOADS_CLI_SL_DOC_BODY_CONFIG = {
  content: { children: [{ content: "`orb` (nameščen tudi kot `orbital`) je ena izvedljiva datoteka. Namestiš ga na strani [namestitev](/sl/docs/getting-started/installation).", direction: "ltr", type: "markdown-content" }, { children: [{ className: "scroll-mt-20", content: "Preverjanje", type: "typography", variant: "h2", weight: "semibold" }, { code: "orb validate app.lolo                 # 0 errors and 0 warnings is the bar\norb validate app.lolo --json          # findings with codes and source positions\norb verify app.lolo                   # play every transition in the Rust kernel\norb verify app.lolo --trait Checkout  # one trait at a time\norb verify app.lolo --all             # the Rust kernel, the JS runtime and the compiled app\norb test app.lolo                     # exhaustive state-machine tests", language: "bash", maxHeight: "520px", showCopyButton: true, showLineNumbers: false, type: "code-block" }], direction: "vertical", gap: "md", id: "check", type: "stack" }, { children: [{ className: "scroll-mt-20", content: "Gradnja in zagon", type: "typography", variant: "h2", weight: "semibold" }, { code: "orb serve app.lolo --open             # compile and serve with the bundled runtime\norb compile app.lolo --output ./out   # write a full TypeScript app\norb compile app.lolo --mode client    # client only: a static site for browser-stored data\norb compile app.lolo --shell python   # other backends: python, rust, mobile, android, ios\norb new my-app                        # scaffold a project", language: "bash", maxHeight: "520px", showCopyButton: true, showLineNumbers: false, type: "code-block" }], direction: "vertical", gap: "md", id: "build", type: "stack" }, { children: [{ className: "scroll-mt-20", content: "Pregledovanje", type: "typography", variant: "h2", weight: "semibold" }, { code: "orb emit orb app.lolo                 # the lowered .orb JSON\norb resolve app.lolo --output app.orb # every import flattened\norb emit ts app.lolo --section Checkout   # generated code for one part\norb execute app.lolo --event PAY --payload '{\"amount\": 40}'\norb format app.lolo                   # pretty-print in place\norb evaluate '(math/clamp 12 0 10)'   # evaluate an expression", language: "bash", maxHeight: "520px", showCopyButton: true, showLineNumbers: false, type: "code-block" }], direction: "vertical", gap: "md", id: "inspect", type: "stack" }, { children: [{ className: "scroll-mt-20", content: "Vedenja in pogoni", type: "typography", variant: "h2", weight: "semibold" }, { code: "orb list --global                     # installed orb packages, and whether each loads\norb add <package>                     # add one to a project\norb engines install --global          # the verify engines for --runtime / --compiled\norb signatures --out signatures.lolo  # the type catalog agents read", language: "bash", maxHeight: "520px", showCopyButton: true, showLineNumbers: false, type: "code-block" }], direction: "vertical", gap: "md", id: "packages", type: "stack" }, { children: [{ className: "scroll-mt-20", content: "Agent", type: "typography", variant: "h2", weight: "semibold" }, { code: "orb \"Build a booking system for a small clinic\"\norb --last \"Add a cancellation fee\"\norb fix app.lolo", language: "bash", maxHeight: "520px", showCopyButton: true, showLineNumbers: false, type: "code-block" }, { content: "Glej [ustvarjanje programov z umetno inteligenco](/sl/docs/tutorials/advanced/ai-generation).", direction: "ltr", type: "markdown-content" }], direction: "vertical", gap: "md", id: "agent", type: "stack" }, { children: [{ className: "scroll-mt-20", content: "Povezava z urejevalniki", type: "typography", variant: "h2", weight: "semibold" }, { code: "orb complete app.lolo --offset 512    # what may be written at a cursor, as JSON\norb embeds app.lolo                   # where each inline behavior embed sits", language: "bash", maxHeight: "520px", showCopyButton: true, showLineNumbers: false, type: "code-block" }, { content: "Izhodne kode: `0` pomeni, da je vse zahtevano uspelo, `1`, da je preverjanje padlo, `2` pa, da orodje ni moglo teči.", direction: "ltr", type: "markdown-content" }], direction: "vertical", gap: "md", id: "editor", type: "stack" }], direction: "vertical", gap: "xl", type: "stack" },
  crumbs: [{ href: "/sl/docs", label: "Dokumentacija" }, { label: "Orodja" }, { label: "Ukazna vrstica" }],
  direction: "ltr",
  lede: "Vsi ukazi orb: preverjanje, testiranje, prevajanje, streženje in orodja okoli njih.",
  navItems: [{ items: [{ active: false, href: "/sl/docs", label: "Pregled" }], label: "Pregled" }, { items: [{ active: false, href: "/sl/docs/getting-started/introduction", label: "Uvod" }, { active: false, href: "/sl/docs/getting-started/installation", label: "Namestitev" }, { active: false, href: "/sl/docs/getting-started/quickstart", label: "Hitri začetek" }, { active: false, href: "/sl/docs/getting-started/project-structure", label: "Zgradba projekta" }], label: "Prvi koraki" }, { items: [{ active: false, href: "/sl/docs/core-concepts/entities", label: "Entitete" }, { active: false, href: "/sl/docs/core-concepts/traits", label: "Lastnosti" }, { active: false, href: "/sl/docs/core-concepts/pages", label: "Strani" }, { active: false, href: "/sl/docs/core-concepts/closed-circuit", label: "Zaprti krog" }, { active: false, href: "/sl/docs/core-concepts/composition", label: "Sestavljanje" }, { active: false, href: "/sl/docs/core-concepts/patterns", label: "Vzorci vmesnika" }, { active: false, href: "/sl/docs/core-concepts/standard-library", label: "Standardna knjižnica" }, { active: false, href: "/sl/docs/core-concepts/localization", label: "Lokalizacija" }], label: "Osnovni pojmi" }, { items: [{ active: false, href: "/sl/docs/tutorials/beginner/complete-orbital", label: "Celoten orbital" }, { active: false, href: "/sl/docs/tutorials/beginner/task-manager", label: "Upravljalnik opravil" }, { active: false, href: "/sl/docs/tutorials/intermediate/ui-patterns", label: "Vzorci vmesnika" }, { active: false, href: "/sl/docs/tutorials/intermediate/guards", label: "Pogoji" }, { active: false, href: "/sl/docs/tutorials/intermediate/cross-orbital", label: "Dogodki med orbitali" }, { active: false, href: "/sl/docs/tutorials/advanced/full-app", label: "Celotna aplikacija" }, { active: false, href: "/sl/docs/tutorials/advanced/ai-generation", label: "Umetna inteligenca" }], label: "Vodniki" }, { items: [{ items: [{ active: false, href: "/sl/docs/reference/operators", label: "Pregled" }, { active: false, href: "/sl/docs/reference/operators/core", label: "Jedro" }, { active: false, href: "/sl/docs/reference/operators/data", label: "Podatki" }, { active: false, href: "/sl/docs/reference/operators/time-format", label: "Čas in oblikovanje" }, { active: false, href: "/sl/docs/reference/operators/validate", label: "Preverjanje" }, { active: false, href: "/sl/docs/reference/operators/flow", label: "Tok" }, { active: false, href: "/sl/docs/reference/operators/random", label: "Naključnost" }, { active: false, href: "/sl/docs/reference/operators/spatial", label: "Prostor" }, { active: false, href: "/sl/docs/reference/operators/ml", label: "Strojno učenje" }, { active: false, href: "/sl/docs/reference/operators/host", label: "Gostitelj" }], label: "Operatorji" }, { items: [{ active: false, href: "/sl/docs/reference/behaviors", label: "Pregled" }, { active: false, href: "/sl/docs/reference/behaviors/ui-core", label: "Jedro vmesnika" }, { active: false, href: "/sl/docs/reference/behaviors/ui-game", label: "Igre" }, { active: false, href: "/sl/docs/reference/behaviors/ui-learning", label: "Učenje" }, { active: false, href: "/sl/docs/reference/behaviors/ui-avl", label: "AVL" }, { active: false, href: "/sl/docs/reference/behaviors/agent", label: "Agenti" }, { active: false, href: "/sl/docs/reference/behaviors/infra", label: "Infrastruktura" }, { active: false, href: "/sl/docs/reference/behaviors/ml", label: "Strojno učenje" }], label: "Vedenja" }, { active: false, href: "/sl/docs/reference/avl", label: "AVL" }, { active: false, href: "/sl/docs/reference/validation", label: "Preverjanje" }], label: "Referenca" }, { items: [{ active: true, href: "/sl/docs/downloads/cli", label: "Ukazna vrstica" }, { active: false, href: "/sl/docs/downloads/skills", label: "Veščine agentov" }], label: "Orodja" }, { items: [{ active: false, href: "/sl/docs/community/contributing", label: "Prispevanje" }, { active: false, href: "/sl/docs/enterprise/index", label: "Za ekipe" }], label: "Skupnost" }],
  next: { href: "/sl/docs/downloads/skills", label: "Veščine agentov" },
  prev: { href: "/sl/docs/reference/validation", label: "Preverjanje" },
  sidebarLabel: "Dokumentacija",
  title: "Ukaz orb",
  toc: [{ id: "check", label: "Preverjanje", level: 2 }, { id: "build", label: "Gradnja in zagon", level: 2 }, { id: "inspect", label: "Pregledovanje", level: 2 }, { id: "packages", label: "Vedenja in pogoni", level: 2 }, { id: "agent", label: "Agent", level: 2 }, { id: "editor", label: "Povezava z urejevalniki", level: 2 }],
  tocLabel: "Na tej strani",
} as OrbDocDownloadsCliSLDocBodyConfig;

export interface OrbDocDownloadsCliSLDocBodyLogicReturn {
  state: OrbDocDownloadsCliSLDocBodyState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: OrbDocDownloadsCliSLDocBodyEvent | null;
  slotWriter: Partial<Record<OrbDocDownloadsCliSLDocBodySlot, OrbDocDownloadsCliSLDocBodyEvent | null>>;
  loading: boolean;
  pendingEvent: OrbDocDownloadsCliSLDocBodyEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<OrbDocDownloadsCliSLDocViewEntity>;
  dispatch: (event: string, payload?: EventPayload) => Promise<EventResponse>;
  actions: { [K in keyof OrbDocDownloadsCliSLDocBodyEventPayloadMap]: (payload?: OrbDocDownloadsCliSLDocBodyEventPayloadMap[K]) => Promise<EventResponse> };
  config: OrbDocDownloadsCliSLDocBodyConfig;
}

export function useOrbDocDownloadsCliSLDocBodyLogic(config?: Partial<OrbDocDownloadsCliSLDocBodyConfig>): OrbDocDownloadsCliSLDocBodyLogicReturn {
  const mergedConfig: OrbDocDownloadsCliSLDocBodyConfig = { ...DEFAULT_ORB_DOC_DOWNLOADS_CLI_SL_DOC_BODY_CONFIG, ...(config ?? {}) };

  // Orbital bridge: event bus + server communication
  const { sendEvent, eventBus } = useOrbitalBridge({ basePath: '/api/orb-doc-downloads-cli-sl-doc-body', orbitalName: 'OrbDocDownloadsCliSL', traitName: 'OrbDocDownloadsCliSLDocBody' });

  // Internal state with reducer
  const [internalState, dispatchReducer] = useReducer(orbDocDownloadsCliSLDocBodyReducer, initialState);
  const { machineState: state, data, lastPayload, lastEvent, slotWriter, loading, pendingEvent, pendingFrom, error, fields: _initialFields } = internalState;
  const fields = _initialFields;
  const internalStateRef = useRef(internalState);
  internalStateRef.current = internalState;

  const machineStateRef = useRef(state);
  machineStateRef.current = state;

  const fieldsRef = useRef<EntityRow>({ ...fields });
  fieldsRef.current = { ...fields };
  const commandPump = useMemo(() => createCommandSendPump(), []);

  // Actor model: sequential event queue (prevents cross-trait race conditions)
  const eventQueueRef = useRef<Array<{ event: string; payload?: EventPayload; settle?: () => void }>>([]);
  const processingRef = useRef(false);

  // Transpiled client-side effects (Phase 4)
  // Each (fromState, event) transition's client effects are compiled to TypeScript.
  const executeTransitionEffects = useCallback((fromState: string, event: string, liveData: Record<string, EntityRow[]>, _payload?: EventPayload): { fields: Record<string, EventPayloadValue | undefined>; newState: string | undefined; emits: Array<() => void> } => {
    const _fields: Record<string, EventPayloadValue | undefined> = {};
    const _emits: Array<() => void> = [];
    let _newState: string | undefined = undefined;
    const entity: EntityRow[] = liveData['OrbDocDownloadsCliSLDocView'] ?? [];
    let fields: Record<string, EventPayloadValue | undefined> = entity[0] ?? {};

    if (fromState === 'idle' && event === 'INIT') {
      // idle → idle (on INIT)
      _newState = 'idle';
    }
    return { fields: _fields, newState: _newState, emits: _emits };
  }, [eventBus]);

  // Per-transition render-ui presence map (codegen).
  // Used by dispatch to skip lastPayload/lastEvent updates for
  // transitions whose only effects are server-side or set/emit —
  // those don't change the rendered JSX, so the view should keep
  // the prior frame's payload until a render-bearing transition
  // (e.g. fetch.emit.success cascade) lands.
  const getRenderUiSlots = useCallback((fromState: string, event: string): { write: OrbDocDownloadsCliSLDocBodySlot[]; clear: OrbDocDownloadsCliSLDocBodySlot[] } => {
    switch (fromState) {
      case 'idle':
        switch (event) {
          case 'INIT': return { write: ['main'] as OrbDocDownloadsCliSLDocBodySlot[], clear: [] as OrbDocDownloadsCliSLDocBodySlot[] };
          default: return { write: [], clear: [] };
        }
      default:
        return { write: [], clear: [] };
    }
  }, []);

  /**
   * Dispatch an event through the orbital bridge.
   * Bridge emits lifecycle events on the bus, forwards to server.
   * Client effects are transpiled at compile time (Phase 4).
   */
  const dispatch = useCallback(async (event: string, __rawPayload?: EventPayload): Promise<EventResponse> => {
    const payload: EventPayload | undefined =
      __rawPayload !== undefined && ('entityId' in __rawPayload || 'id' in __rawPayload)
        ? __rawPayload
        : config?.entityId !== undefined
          ? { ...(__rawPayload ?? {}), entityId: config.entityId }
          : __rawPayload;
    const prevState = machineStateRef.current;
    // T9 optimistic dispatch: the fields mirror IS the [runtime] entity
    // row, so guard + to-state resolve locally on the same data the
    // server would read. Commit first, POST after; reconcile below.
    const liveData: Record<string, EntityRow[]> = { 'OrbDocDownloadsCliSLDocView': [fieldsRef.current as EntityRow] };
    const _optimistic = executeTransitionEffects(prevState, event, liveData, payload);
    if (_optimistic.newState === undefined) {
      // Guard rejected or no transition defined — mirrors the server's
      // rejection, without the round trip.
      const errorMsg = `No transition defined for ${event} from ${prevState} (or guard rejected)`;
      dispatchReducer({ type: 'EVENT_ERROR', error: errorMsg });
      return { success: false, transitioned: false, states: {}, emittedEvents: [], error: errorMsg } as EventResponse;
    }
    const _renderSlots = getRenderUiSlots(prevState, event);
    const _renderBearing = _renderSlots.write.length > 0 || _renderSlots.clear.length > 0;
    // Snapshot the touched fields' prior values for a reconcile rollback.
    const _snapshot: Record<string, EventPayloadValue | undefined> = {};
    for (const _k of Object.keys(_optimistic.fields)) { _snapshot[_k] = fieldsRef.current[_k]; }
    dispatchReducer({
      type: 'EVENT_SUCCESS',
      event: event as OrbDocDownloadsCliSLDocBodyEvent,
      newState: _optimistic.newState,
      data: {},
      payload,
      fields: _optimistic.fields,
      renderBearing: _renderBearing,
      renderWrite: _renderSlots.write,
      renderClear: _renderSlots.clear,
    });
    machineStateRef.current = _optimistic.newState as typeof machineStateRef.current;
    fieldsRef.current = { ...fieldsRef.current, ..._optimistic.fields } as EntityRow;
    for (const _emit of _optimistic.emits) _emit();

    void sendEvent; void commandPump; // no server-touching effect on this trait; shared-store commit above is the only sync needed
    return { success: true, transitioned: true, states: { 'OrbDocDownloadsCliSLDocBody': _optimistic.newState }, emittedEvents: [] } as EventResponse;
  }, [sendEvent, executeTransitionEffects, commandPump, config?.entityId]);

  // Actor model: drain event queue one at a time
  const drainQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    try {
      while (eventQueueRef.current.length > 0) {
        const entry = eventQueueRef.current.shift()!;
        try { await dispatch(entry.event, entry.payload); } finally { entry.settle?.(); }
      }
    } finally {
      processingRef.current = false;
    }
  }, [dispatch]);

  // Actor model: enqueue event and start drain
  const enqueueEvent = useCallback((event: string, payload?: EventPayload) => {
    return new Promise<void>((settle) => {
    eventQueueRef.current.push({ event, payload, settle });
    void drainQueue();
    });
  }, [drainQueue]);

  // Action creators (typed per-event)
  const actions: { [K in keyof OrbDocDownloadsCliSLDocBodyEventPayloadMap]: (payload?: OrbDocDownloadsCliSLDocBodyEventPayloadMap[K]) => Promise<EventResponse> } = {
    'INIT': () => dispatch('INIT'),
  };

  useUIEvents(enqueueEvent, 'OrbDocDownloadsCliSL.OrbDocDownloadsCliSLDocBody', ['INIT'] as const, eventBus);

  // Verifier snapshot registration (VG Foundation 1)
  useEffect(() => {
    const unregister = registerTraitSnapshot('OrbDocDownloadsCliSLDocBody', (): TraitStateSnapshot => ({
      traitName: 'OrbDocDownloadsCliSLDocBody',
      currentState: internalStateRef.current.machineState,
      states: ['idle'],
      events: ['INIT'],
      data: internalStateRef.current.data,
      lastPayload: internalStateRef.current.lastPayload,
      lastEventDispatched: internalStateRef.current.lastEvent !== null
        ? {
            event: internalStateRef.current.lastEvent,
            ...(internalStateRef.current.lastPayload !== undefined
              ? { payload: internalStateRef.current.lastPayload }
              : {}),
            timestamp: Date.now(),
          }
        : undefined,
      cascadeReceived: [],
    }));
    return unregister;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-init on mount
  useEffect(() => {
    const __rp = Object.fromEntries(Object.entries(config?.routeParams ?? {}).filter(([, v]) => v !== undefined));
    const __init = { ...__rp, ...(config?.initPayload ?? {}), ...(config?.entityId !== undefined ? { entityId: config.entityId } : {}) };
    dispatch('INIT', Object.keys(__init).length > 0 ? __init : undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    state,
    data,
    lastPayload,
    lastEvent,
    slotWriter,
    loading,
    pendingEvent,
    pendingFrom,
    error,
    fields,
    dispatch,
    actions,
    config: mergedConfig,
  };
}
